import { buildFavoriteShopSnapshot } from '@/lib/favorites';
import { CompanyService } from '@/services/company.service';
import type { FavoriteCompaniesCountDto, FavoriteCompanyDto } from '@/types/dto/company-favorite.dto';
import { ListCompaniesParams } from '@/types/dto/ListCompaniesParams.dto';
import type { FavoriteShopSnapshot } from '@/types/profile';
import { queryOptions } from '@tanstack/react-query';

const COMPANY_QUERY_STALE_TIME = 1000 * 60 * 2;
const COMPANY_QUERY_DEFAULTS = {
  retry: false,
  staleTime: COMPANY_QUERY_STALE_TIME,
  refetchOnWindowFocus: false,
} as const;

// `queryKey` only identifies the cache bucket.
export const companyQueryKeys = {
  all: ['company'] as const,
  lists: () => [...companyQueryKeys.all, 'list'] as const,
  list: (params: ListCompaniesParams) => [...companyQueryKeys.lists(), params] as const,
  details: () => [...companyQueryKeys.all, 'detail'] as const,
  bySlug: (slug?: string) => [...companyQueryKeys.details(), 'slug', slug ?? null] as const,
  favorites: (viewerId?: string | null) => [...companyQueryKeys.all, 'favorites', viewerId ?? null] as const,
  favoritesCount: (viewerId?: string | null) => [...companyQueryKeys.all, 'favorites-count', viewerId ?? null] as const,
  services: (companyId?: string) => [...companyQueryKeys.all, 'services', companyId ?? null] as const,
  professionals: (companyId?: string) => [...companyQueryKeys.all, 'professionals', companyId ?? null] as const,
  reviews: (companyId?: string) => [...companyQueryKeys.all, 'reviews', companyId ?? null] as const,
};

// `queryOptions` connects a cache key with the fetcher and shared defaults.
export const companyQueryOptions = {
  list: (params: ListCompaniesParams) =>
    queryOptions({
      queryKey: companyQueryKeys.list(params),
      queryFn: () => CompanyService.list(params),
      ...COMPANY_QUERY_DEFAULTS,
    }),
  bySlug: (slug?: string) =>
    queryOptions({
      queryKey: companyQueryKeys.bySlug(slug),
      enabled: Boolean(slug),
      queryFn: async () => {
        if (!slug) {
          throw new Error('Company slug is required');
        }

        return CompanyService.getBySlug(slug);
      },
      ...COMPANY_QUERY_DEFAULTS,
    }),
  favorites: (viewerId?: string | null, enabled = true) =>
    queryOptions<FavoriteShopSnapshot[]>({
      queryKey: companyQueryKeys.favorites(viewerId),
      enabled: enabled && Boolean(viewerId),
      queryFn: async () => {
        const favorites = await CompanyService.listFavorites();

        return favorites.map((favorite: FavoriteCompanyDto) => buildFavoriteShopSnapshot(favorite));
      },
      ...COMPANY_QUERY_DEFAULTS,
    }),
  favoritesCount: (viewerId?: string | null, enabled = true) =>
    queryOptions<FavoriteCompaniesCountDto>({
      queryKey: companyQueryKeys.favoritesCount(viewerId),
      enabled: enabled && Boolean(viewerId),
      queryFn: CompanyService.getFavoritesCount,
      ...COMPANY_QUERY_DEFAULTS,
    }),
  services: (companyId?: string) =>
    queryOptions({
      queryKey: companyQueryKeys.services(companyId),
      enabled: Boolean(companyId),
      queryFn: async () => {
        if (!companyId) {
          throw new Error('Company ID is required');
        }

        return CompanyService.getServices(companyId);
      },
      ...COMPANY_QUERY_DEFAULTS,
    }),
  professionals: (companyId?: string) =>
    queryOptions({
      queryKey: companyQueryKeys.professionals(companyId),
      enabled: Boolean(companyId),
      queryFn: async () => {
        if (!companyId) {
          throw new Error('Company ID is required');
        }

        return CompanyService.getProfessionals(companyId);
      },
      ...COMPANY_QUERY_DEFAULTS,
    }),
};
